import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useDebounce } from './useDebounce'
import {
  listStoreGroups,
  getStoreGroupDetail,
  updateStoreGroup,
  listStoreGroupStores,
} from '@/api/storeGroups'
import { queryKeys } from '@/lib/queryKeys'
import type {
  PaginatedStoreGroupsResponse,
  StoreGroupDetailRead,
  StoreGroupRead,
  StoreGroupMemberListResponse,
  StoreGroupUpdate,
  GroupingMethod,
} from '@/types'
import type { AxiosError } from 'axios'

interface UseStoreGroupsListParams {
  search: string
  storeModelId: string
  groupingMethod: GroupingMethod | 'all'
  page: number
  pageSize: number
}

export function useStoreGroupsList({
  search,
  storeModelId,
  groupingMethod,
  page,
  pageSize,
}: UseStoreGroupsListParams) {
  const debouncedSearch = useDebounce(search, 300)
  const debouncedModelId = useDebounce(storeModelId, 300)

  const filters = {
    search: debouncedSearch,
    storeModelId: debouncedModelId,
    groupingMethod: groupingMethod === 'all' ? undefined : groupingMethod,
    page,
    pageSize,
  }

  return useQuery<PaginatedStoreGroupsResponse>({
    queryKey: queryKeys.storeGroups.list(filters),
    queryFn: () =>
      listStoreGroups({
        search: debouncedSearch || undefined,
        store_model_id: debouncedModelId || undefined,
        grouping_method: groupingMethod === 'all' ? undefined : groupingMethod,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      }),
  })
}

export function useStoreGroupDetail(groupId: string | undefined) {
  return useQuery<StoreGroupDetailRead>({
    queryKey: queryKeys.storeGroups.detail(groupId ?? ''),
    queryFn: () => getStoreGroupDetail(groupId!),
    enabled: !!groupId,
    staleTime: 1000 * 60,
  })
}

interface UseStoreGroupStoresParams {
  groupId: string
  search: string
  region: string
  page: number
  pageSize: number
}

export function useStoreGroupStores({
  groupId,
  search,
  region,
  page,
  pageSize,
}: UseStoreGroupStoresParams) {
  const debouncedSearch = useDebounce(search, 300)

  const filters = {
    search: debouncedSearch,
    region,
    page,
    pageSize,
  }

  return useQuery<StoreGroupMemberListResponse>({
    queryKey: queryKeys.storeGroups.stores(groupId, filters),
    queryFn: () =>
      listStoreGroupStores({
        groupId,
        search: debouncedSearch || undefined,
        region: region || undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
      }),
    enabled: !!groupId,
    staleTime: 1000 * 60,
  })
}

export function useRenameStoreGroup() {
  const queryClient = useQueryClient()
  return useMutation<
    StoreGroupRead,
    AxiosError,
    { groupId: string; payload: StoreGroupUpdate; rowVersion?: number }
  >({
    mutationFn: ({ groupId, payload, rowVersion }) =>
      updateStoreGroup({ groupId, payload, rowVersion }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeGroups.detail(variables.groupId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.storeGroups.all,
      })
    },
  })
}
